import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import type { SimulationScenario, ScenariosCollection } from './types';
import {
  getScenariosCollection,
  getAllScenariosCollections,
  createScenariosCollection,
  deleteCollection,
  listScenarios,
  addScenario,
  updateScenario,
  deleteScenario,
  seedScenarioFromJson,
} from './scenariosCollection';
import { runUnifiedSimulation } from '../../scripts/simulationEngine';
import './SimulationScenariosPage.css';
import * as PlatformApi from '@dtplatform/platform-api';

export const SimulationScenariosPage: React.FC = () => {
  const { createCtx, accessToken } = useAuth();
  const { selectedProject, applicationNamespaces } = useApp();
  const [loading, setLoading] = useState(true);
  const [collection, setCollection] = useState<ScenariosCollection | null>(null);
  const [scenarios, setScenarios] = useState<SimulationScenario[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formEquipmentJson, setFormEquipmentJson] = useState('[]');
  const [formInteractionsJson, setFormInteractionsJson] = useState('[]');
  const [jsonError, setJsonError] = useState('');
  const [modalTab, setModalTab] = useState<'basic' | 'equipment' | 'interactions'>('basic');

  // Dry run state
  const [isRunningDryRun, setIsRunningDryRun] = useState(false);
  const [dryRunResults, setDryRunResults] = useState<any>(null);
  const [isResultsModalOpen, setIsResultsModalOpen] = useState(false);

  // Get single project namespace (notification service requires single namespace)
  const getProjectNamespace = useCallback((): string[] => {
    const namespaces = selectedProject?._namespaces || [];
    // Return only the first namespace (project namespace)
    return namespaces.length > 0 ? [namespaces[0]] : [];
  }, [selectedProject]);

  // Create context for API calls - use ONLY project namespace (no baseNamespaces)
  const getCtx = useCallback(() => {
    const namespaces = getProjectNamespace();
    return {
      authToken: accessToken || '',
      _namespaces: namespaces,
    };
  }, [accessToken, getProjectNamespace]);

  // Check for existing collection without auto-creating
  useEffect(() => {
    if (!accessToken || !selectedProject) return;
    checkForCollection();
  }, [accessToken, selectedProject]);

  const checkForCollection = async () => {
    if (!accessToken || !selectedProject) return;
    setLoading(true);
    try {
      const ctx = getCtx();
      const coll = await getScenariosCollection(ctx);
      setCollection(coll);
      if (coll) {
        const list = await listScenarios(coll, ctx);
        setScenarios(list);
      }
    } catch (e) {
      console.error('Failed to check for scenarios collection', e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCollection = async () => {
    if (!accessToken || !selectedProject) return;
    setLoading(true);
    try {
      const ctx = getCtx();
      const namespaces = selectedProject?._namespaces || [];
      const coll = await createScenariosCollection(ctx, namespaces);
      setCollection(coll);
      if (coll) {
        const list = await listScenarios(coll, ctx);
        setScenarios(list);
      }
    } catch (e) {
      console.error('Failed to create scenarios collection', e);
      alert('Failed to create collection');
    } finally {
      setLoading(false);
    }
  };

  const handleRecreateCollection = async () => {
    if (!accessToken || !selectedProject) return;
    if (!confirm('This will DELETE all existing scenarios collections and create a new one. Continue?')) return;

    setLoading(true);
    try {
      const ctx = getCtx();

      // Get all collections and delete them
      const allCollections = await getAllScenariosCollections(ctx);
      for (const coll of allCollections) {
        if (coll._id) {
          await deleteCollection(coll._id, ctx);
        }
      }

      // Create new collection
      const namespaces = selectedProject?._namespaces || [];
      const newColl = await createScenariosCollection(ctx, namespaces);
      setCollection(newColl);
      setScenarios([]);
      alert(`Deleted ${allCollections.length} collection(s) and created a new one`);
    } catch (e) {
      console.error('Failed to recreate scenarios collection', e);
      alert('Failed to recreate collection');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = useCallback(() => {
    setEditingId(null);
    setFormName('');
    setFormDescription('');
    setFormEquipmentJson('[]');
    setFormInteractionsJson('[]');
    setJsonError('');
    setModalTab('basic');
    setIsModalOpen(true);
  }, []);

  const handleEdit = useCallback((scenario: SimulationScenario) => {
    setEditingId(scenario._id || null);
    setFormName(scenario.name);
    setFormDescription(scenario.description);
    setFormEquipmentJson(JSON.stringify(scenario.equipment || [], null, 2));
    setFormInteractionsJson(JSON.stringify(scenario.equipmentInteractions || [], null, 2));
    setJsonError('');
    setModalTab('basic');
    setIsModalOpen(true);
  }, []);

  const handleSave = async () => {
    if (!accessToken || !selectedProject || !collection) return;
    if (!formName.trim()) {
      alert('Scenario name is required');
      return;
    }

    // Parse equipment JSON
    let equipment = [];
    try {
      equipment = JSON.parse(formEquipmentJson);
      if (!Array.isArray(equipment)) {
        setJsonError('Equipment must be an array');
        return;
      }
      setJsonError('');
    } catch (e) {
      setJsonError('Invalid JSON: ' + (e as Error).message);
      return;
    }

    // Parse interactions JSON
    let equipmentInteractions = [];
    try {
      equipmentInteractions = JSON.parse(formInteractionsJson);
      if (!Array.isArray(equipmentInteractions)) {
        setJsonError('Equipment interactions must be an array');
        return;
      }
      setJsonError('');
    } catch (e) {
      setJsonError('Invalid interactions JSON: ' + (e as Error).message);
      return;
    }

    try {
      const ctx = getCtx();
      const scenarioData: SimulationScenario = {
        name: formName.trim(),
        description: formDescription.trim(),
        equipment,
        sensorCorrelations: [],
        equipmentInteractions,
      };

      if (editingId) {
        await updateScenario(collection, editingId, scenarioData, ctx);
      } else {
        await addScenario(collection, scenarioData, ctx);
      }

      const list = await listScenarios(collection, ctx);
      setScenarios(list);
      setIsModalOpen(false);
      setEditingId(null);
      setFormName('');
      setFormDescription('');
      setFormEquipmentJson('[]');
      setJsonError('');
    } catch (e) {
      console.error('Failed to save scenario', e);
      alert('Failed to save scenario');
    }
  };

  const handleDelete = async (scenarioId: string) => {
    if (!accessToken || !selectedProject || !collection) return;
    if (!confirm('Are you sure you want to delete this scenario?')) return;

    try {
      const ctx = getCtx();
      await deleteScenario(collection, scenarioId, ctx);
      const list = await listScenarios(collection, ctx);
      setScenarios(list);
    } catch (e) {
      console.error('Failed to delete scenario', e);
      alert('Failed to delete scenario');
    }
  };

  const handleSeedDefaults = async () => {
    if (!accessToken || !selectedProject || !collection) return;
    if (!confirm('This will create a default scenario from equipment seed data. Continue?')) return;

    try {
      const ctx = getCtx();
      const count = await seedScenarioFromJson(collection, ctx);
      if (count > 0) {
        const list = await listScenarios(collection, ctx);
        setScenarios(list);
        alert(`Created ${count} default scenario`);
      } else {
        alert('Default scenario already exists or no data to seed');
      }
    } catch (e) {
      console.error('Failed to seed defaults', e);
      alert('Failed to seed defaults');
    }
  };

  const handleDryRun = async (scenario: SimulationScenario) => {
    if (!accessToken || !selectedProject) return;
    if (!scenario.equipment || scenario.equipment.length === 0) {
      alert('Scenario has no equipment configured. Please add equipment first.');
      return;
    }

    setIsRunningDryRun(true);
    setDryRunResults(null);

    try {
      const ctx = getCtx();
      const now = new Date();
      const oneMinuteAgo = new Date(now.getTime() - 60 * 1000);

      const results = await runUnifiedSimulation({
        scenario,
        simPeriod: {
          from: oneMinuteAgo.toISOString(),
          to: now.toISOString()
        },
        dryRun: true,
        stepSeconds: 5
      }, {PlatformApi}, ctx);

      setDryRunResults(results);
      setIsResultsModalOpen(true);
    } catch (e) {
      console.error('Dry run failed', e);
      alert('Dry run failed: ' + (e as Error).message);
    } finally {
      setIsRunningDryRun(false);
    }
  };

  if (loading) {
    return (
      <div className="simulation-scenarios-page">
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <span>Loading scenarios...</span>
        </div>
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="simulation-scenarios-page">
        <div className="page-header">
          <div className="header-content">
            <h1>
              <i className="fas fa-project-diagram"></i>
              Simulation Scenarios
            </h1>
            <p className="page-description">
              Manage simulation scenarios for equipment and building systems
            </p>
          </div>
        </div>
        <div className="empty-state">
          <i className="fas fa-database"></i>
          <h3>No Collection Found</h3>
          <p>Create a scenarios collection to get started</p>
          <button className="btn btn-primary" onClick={handleCreateCollection}>
            <i className="fas fa-plus-circle"></i>
            Create Collection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="simulation-scenarios-page">
      <div className="page-header">
        <div className="header-content">
          <h1>
            <i className="fas fa-project-diagram"></i>
            Simulation Scenarios
          </h1>
          <p className="page-description">
            Manage simulation scenarios for equipment and building systems
          </p>
        </div>
        <div className="header-actions">
          <button className="btn btn-danger btn-sm" onClick={handleRecreateCollection} title="Delete all collections and create new one">
            <i className="fas fa-redo"></i>
            Recreate Collection
          </button>
          <button className="btn btn-secondary" onClick={handleSeedDefaults}>
            <i className="fas fa-seedling"></i>
            Seed Defaults
          </button>
          <button className="btn btn-primary" onClick={handleAdd}>
            <i className="fas fa-plus"></i>
            Add Scenario
          </button>
        </div>
      </div>

      {scenarios.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-project-diagram"></i>
          <h3>No Scenarios Yet</h3>
          <p>Create your first simulation scenario to get started</p>
          <button className="btn btn-primary" onClick={handleAdd}>
            <i className="fas fa-plus"></i>
            Add Scenario
          </button>
        </div>
      ) : (
        <div className="scenarios-grid">
          {scenarios.map((scenario) => (
            <div key={scenario._id} className="scenario-card">
              <div className="scenario-header">
                <h3>{scenario.name}</h3>
                <div className="scenario-actions">
                  <button
                    className="btn-icon"
                    onClick={() => handleEdit(scenario)}
                    title="Edit scenario"
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                  <button
                    className="btn-icon btn-danger"
                    onClick={() => scenario._id && handleDelete(scenario._id)}
                    title="Delete scenario"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>

              {scenario.description && (
                <p className="scenario-description">{scenario.description}</p>
              )}

              <div className="scenario-stats">
                <div className="stat-item">
                  <i className="fas fa-cog"></i>
                  <span>{scenario.equipment?.length || 0} Equipment</span>
                </div>
                <div className="stat-item">
                  <i className="fas fa-exchange-alt"></i>
                  <span>{scenario.equipmentInteractions?.length || 0} Interactions</span>
                </div>
              </div>

              {scenario.updatedAt && (
                <div className="scenario-metadata">
                  <small>Updated: {new Date(scenario.updatedAt).toLocaleDateString()}</small>
                </div>
              )}

              <div className="scenario-card-actions">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleDryRun(scenario)}
                  disabled={isRunningDryRun || !scenario.equipment || scenario.equipment.length === 0}
                  title="Run simulation in dry-run mode (1 minute)"
                >
                  {isRunningDryRun ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Running...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-play-circle"></i>
                      Dry Run
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <i className={editingId ? 'fas fa-edit' : 'fas fa-plus'}></i>
                {editingId ? 'Edit Scenario' : 'Add Scenario'}
              </h2>
              <button
                className="btn-close"
                onClick={() => setIsModalOpen(false)}
                title="Close"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-tabs">
              <button
                className={`modal-tab ${modalTab === 'basic' ? 'active' : ''}`}
                onClick={() => setModalTab('basic')}
              >
                <i className="fas fa-info-circle"></i>
                Basic Info
              </button>
              <button
                className={`modal-tab ${modalTab === 'equipment' ? 'active' : ''}`}
                onClick={() => setModalTab('equipment')}
              >
                <i className="fas fa-cog"></i>
                Equipment ({JSON.parse(formEquipmentJson || '[]').length})
              </button>
              <button
                className={`modal-tab ${modalTab === 'interactions' ? 'active' : ''}`}
                onClick={() => setModalTab('interactions')}
              >
                <i className="fas fa-exchange-alt"></i>
                Interactions ({JSON.parse(formInteractionsJson || '[]').length})
              </button>
            </div>

            <div className="modal-body">
              {modalTab === 'basic' && (
                <>
                  <div className="form-group">
                    <label htmlFor="scenario-name">
                      Scenario Name <span className="required">*</span>
                    </label>
                    <input
                      id="scenario-name"
                      type="text"
                      className="form-control"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      placeholder="Enter scenario name"
                      autoFocus
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="scenario-description">Description</label>
                    <textarea
                      id="scenario-description"
                      className="form-control"
                      value={formDescription}
                      onChange={(e) => setFormDescription(e.target.value)}
                      placeholder="Enter scenario description"
                      rows={4}
                    />
                  </div>

                  <div className="info-box">
                    <i className="fas fa-info-circle"></i>
                    <p>
                      Use the Equipment tab to configure equipment instances, power profiles,
                      sensors, control rules, and anomaly settings.
                    </p>
                  </div>
                </>
              )}

              {modalTab === 'equipment' && (
                <>
                  <div className="form-group">
                    <label htmlFor="equipment-json">
                      Equipment Configuration (JSON)
                    </label>
                    <textarea
                      id="equipment-json"
                      className={`form-control code-editor ${jsonError ? 'error' : ''}`}
                      value={formEquipmentJson}
                      onChange={(e) => {
                        setFormEquipmentJson(e.target.value);
                        setJsonError('');
                      }}
                      placeholder='[{"id": "AHU-1", "equipmentTypeCode": "AHU", ...}]'
                      rows={20}
                      spellCheck={false}
                      style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                    />
                    {jsonError && (
                      <div className="error-message">
                        <i className="fas fa-exclamation-circle"></i>
                        {jsonError}
                      </div>
                    )}
                  </div>

                  <div className="info-box">
                    <i className="fas fa-lightbulb"></i>
                    <p>
                      <strong>Equipment Instance Format:</strong> Each equipment object needs: id,
                      equipmentTypeCode, equipmentTypeName, category, powerProfile, dataPoints, anomalyConfig.
                      See types.ts for full ScenarioEquipmentInstance interface.
                    </p>
                  </div>
                </>
              )}

              {false && <div />}

              {modalTab === 'interactions' && (
                <>
                  <div className="form-group">
                    <label htmlFor="interactions-json">
                      Equipment Interactions (JSON)
                    </label>
                    <textarea
                      id="interactions-json"
                      className={`form-control code-editor ${jsonError ? 'error' : ''}`}
                      value={formInteractionsJson}
                      onChange={(e) => {
                        setFormInteractionsJson(e.target.value);
                        setJsonError('');
                      }}
                      placeholder='[{"fromEquipmentType": "HYDRONIC_CHILLER", "toEquipmentType": "AHU", "sensorLinks": [{"fromSensor": "chilled-water-leaving-temp", "toSensor": "discharge-air-temp", "factor": 0.1}]}]'
                      rows={20}
                      spellCheck={false}
                      style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                    />
                    {jsonError && (
                      <div className="error-message">
                        <i className="fas fa-exclamation-circle"></i>
                        {jsonError}
                      </div>
                    )}
                  </div>

                  <div className="info-box">
                    <i className="fas fa-lightbulb"></i>
                    <p>
                      <strong>Equipment Interaction Format:</strong> Defines how equipment affects each other.
                      Use equipment type codes (e.g., "HYDRONIC_CHILLER", "AHU") and Haystack sensor tags.
                      Factor determines strength of interaction (0.1 = 10% influence).
                    </p>
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSave}>
                <i className="fas fa-save"></i>
                {editingId ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isResultsModalOpen && dryRunResults && (
        <div className="modal-overlay" onClick={() => setIsResultsModalOpen(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <i className="fas fa-chart-line"></i>
                Dry Run Results
              </h2>
              <button
                className="btn-close"
                onClick={() => setIsResultsModalOpen(false)}
                title="Close"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="results-summary">
                <div className="summary-item">
                  <label>Simulation Period:</label>
                  <span>{new Date(dryRunResults.simPeriodUsed.from).toLocaleString()} → {new Date(dryRunResults.simPeriodUsed.to).toLocaleString()}</span>
                </div>
                <div className="summary-item">
                  <label>Total Readings Generated:</label>
                  <span>{dryRunResults.results.length} readings</span>
                </div>
                <div className="summary-item">
                  <label>Equipment Simulated:</label>
                  <span>{dryRunResults.results[0]?.equipment?.length || 0} equipment instances</span>
                </div>
              </div>

              <div className="results-tabs">
                <h3>Sample Readings</h3>
                <div className="results-grid">
                  {dryRunResults.results.slice(0, 3).map((reading: any, idx: number) => (
                    <div key={idx} className="result-card">
                      <div className="result-header">
                        <strong>Timestamp:</strong> {new Date(reading.timestamp).toLocaleTimeString()}
                      </div>
                      <div className="result-metrics">
                        <div className="metric-row">
                          <span>Total Power:</span>
                          <span className="metric-value">{reading.totalPower.toFixed(2)} kW</span>
                        </div>
                        <div className="metric-row">
                          <span>Step Energy:</span>
                          <span className="metric-value">{reading.stepEnergy.toFixed(4)} kWh</span>
                        </div>
                      </div>
                      {reading.equipment && (
                        <div className="equipment-list">
                          {reading.equipment.map((eq: any, eqIdx: number) => (
                            <details key={eqIdx} className="equipment-details">
                              <summary>
                                <i className="fas fa-cog"></i>
                                {eq.equipmentId} ({eq.equipmentType})
                                {eq.anomalyInjected && <span className="anomaly-badge">Anomaly</span>}
                              </summary>
                              <div className="sensor-readings">
                                {Object.entries(eq.sensors).map(([sensor, value]: [string, any]) => (
                                  <div key={sensor} className="sensor-row">
                                    <span className="sensor-name">{sensor}:</span>
                                    <span className="sensor-value">{typeof value === 'number' ? value.toFixed(2) : value}</span>
                                  </div>
                                ))}
                              </div>
                            </details>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="info-box">
                <i className="fas fa-info-circle"></i>
                <p>
                  <strong>Dry Run Mode:</strong> Simulation ran successfully without writing to database.
                  Total of {dryRunResults.results.length} readings generated over 1 minute period.
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setIsResultsModalOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
